import { CloseCircleFilled } from "@ant-design/icons";
import { useKeyboardModalProps } from "@org-avp/avp-avengers-ui-shared-components";
import {
  discardFirstErrorResponse,
  ErrorResponseType,
  ErrorResponseTypes,
  PayloadTooLargeErrorResponse,
  selectFirstErrorResponse,
  TimeoutErrorResponse,
  useAppDispatch,
} from "@org-avp/avp-avengers-ui-shared-store";
import { ButtonProps, Modal } from "antd";
import { FC, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AdviseErrorResponseModal } from "./AdviseErrorResponseModal";
import styles from "./ErrorResponseDialog.module.less";
import { TitleMessage } from "./TitleMessage";

export interface ErrorResponseModalProps<T extends ErrorResponseTypes> {
  errorResponse: T;
  onOk: () => void;
}

export const ErrorResponseDialog = () => {
  const appDispatch = useAppDispatch();
  const errorResponse = useSelector(selectFirstErrorResponse);
  useEffect(
    () => {
      // discard unknown error responses for now (only show advise errors and the error types defined below)
      if (errorResponse?.type === ErrorResponseType.UNKNOWN) {
        appDispatch(discardFirstErrorResponse());
      }
    },
    [appDispatch, errorResponse],
  );

  const onOk = useCallback(
    () => appDispatch(discardFirstErrorResponse()),
    [appDispatch],
  );

  return (
    <>
      {errorResponse?.type === ErrorResponseType.ADVISE && <AdviseErrorResponseModal errorResponse={errorResponse} onOk={onOk}/>}
      {errorResponse?.type === ErrorResponseType.TIMEOUT && <TimeoutErrorResponseModal errorResponse={errorResponse} onOk={onOk}/>}
      {errorResponse?.type === ErrorResponseType.PAYLOAD_TOO_LARGE && <PayloadTooLargeErrorResponseModal errorResponse={errorResponse} onOk={onOk}/>}
    </>
  );
};

type TimeoutErrorResponseModalProps = ErrorResponseModalProps<TimeoutErrorResponse>;
export const TimeoutErrorResponseModal: FC<TimeoutErrorResponseModalProps> = ({ onOk }) =>
  <TypedErrorResponseModal tPrefix="errorResponse.timeout" onOk={onOk}/>;

type PayloadTooLargeErrorResponseModalProps = ErrorResponseModalProps<PayloadTooLargeErrorResponse>;
export const PayloadTooLargeErrorResponseModal: FC<PayloadTooLargeErrorResponseModalProps> = ({ onOk }) =>
  <TypedErrorResponseModal tPrefix="errorResponse.payloadTooLarge" onOk={onOk}/>;

const cancelButtonProps: ButtonProps = { style: { display: "none" } };

interface TypedErrorResponseModalProps {
  tPrefix: string;
  onOk: () => void;
}

const TypedErrorResponseModal: FC<TypedErrorResponseModalProps> = ({ tPrefix, onOk }) => {
  const { t } = useTranslation("app", { keyPrefix: tPrefix });
  const modalProps = useKeyboardModalProps({
    cancelButtonProps,
    centered: true,
    closeIcon: false,
    open: true,
  }, onOk);

  return (
    <Modal {...modalProps}>
      <div className={styles.content}>
        <div className={styles.icon}><CloseCircleFilled/></div>
        <div className={styles.errorContainer}>
          <TitleMessage title={t("title")} message={t("message")}/>
        </div>
      </div>
    </Modal>
  );
};
